import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchTodosApi,
  createTodoApi,
  updateTodoApi,
  deleteTodoApi,
  createTodoFromVorlageApi,
  fetchKategorienApi,
  createKategorieApi,
  updateKategorieApi,
  deleteKategorieApi,
  fetchVorlagenApi,
  createVorlageApi,
  updateVorlageApi,
  deleteVorlageApi,
} from './todoApi';

/**
 * Redux-Slice fuer das TODO-Modul (Aufgaben, Kategorien, Vorlagen).
 */

// ── Aufgaben ────────────────────────────────────────────
export const fetchTodos = createAsyncThunk('todo/fetchTodos', async () => (await fetchTodosApi()) ?? []);
export const createTodo = createAsyncThunk('todo/createTodo', async (todo) => createTodoApi(todo));
export const updateTodo = createAsyncThunk('todo/updateTodo', async ({ uuid, todo }) => updateTodoApi(uuid, todo));
export const deleteTodo = createAsyncThunk('todo/deleteTodo', async (uuid) => deleteTodoApi(uuid));
export const createTodoFromVorlage = createAsyncThunk(
  'todo/createFromVorlage',
  async (vorlageUuid) => createTodoFromVorlageApi(vorlageUuid)
);

// ── Kategorien ──────────────────────────────────────────
export const fetchKategorien = createAsyncThunk('todo/fetchKategorien', async () => (await fetchKategorienApi()) ?? []);
export const createKategorie = createAsyncThunk('todo/createKategorie', async (k) => createKategorieApi(k));
export const updateKategorie = createAsyncThunk('todo/updateKategorie', async ({ uuid, kategorie }) => updateKategorieApi(uuid, kategorie));
export const deleteKategorie = createAsyncThunk('todo/deleteKategorie', async (uuid) => deleteKategorieApi(uuid));

// ── Vorlagen ────────────────────────────────────────────
export const fetchVorlagen = createAsyncThunk('todo/fetchVorlagen', async () => (await fetchVorlagenApi()) ?? []);
export const createVorlage = createAsyncThunk('todo/createVorlage', async (v) => createVorlageApi(v));
export const updateVorlage = createAsyncThunk('todo/updateVorlage', async ({ uuid, vorlage }) => updateVorlageApi(uuid, vorlage));
export const deleteVorlage = createAsyncThunk('todo/deleteVorlage', async (uuid) => deleteVorlageApi(uuid));

const initialState = {
  todos: [],
  kategorien: [],
  vorlagen: [],
  status: 'idle', // idle | loading | succeeded | failed
  error: null,
};

/** Ersetzt ein Element (per uuid) in einer Liste oder haengt es an. */
const upsert = (liste, element) => {
  if (!element) return;
  const idx = liste.findIndex((e) => e.uuid === element.uuid);
  if (idx >= 0) {
    liste[idx] = element;
  } else {
    liste.push(element);
  }
};

const todoSlice = createSlice({
  name: 'todo',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTodos.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchTodos.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.todos = action.payload;
      })
      .addCase(fetchTodos.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(createTodo.fulfilled, (state, action) => upsert(state.todos, action.payload))
      .addCase(updateTodo.fulfilled, (state, action) => upsert(state.todos, action.payload))
      .addCase(createTodoFromVorlage.fulfilled, (state, action) => upsert(state.todos, action.payload))
      .addCase(deleteTodo.fulfilled, (state, action) => {
        state.todos = state.todos.filter((t) => t.uuid !== action.payload);
      })
      .addCase(fetchKategorien.fulfilled, (state, action) => {
        state.kategorien = action.payload;
      })
      .addCase(createKategorie.fulfilled, (state, action) => upsert(state.kategorien, action.payload))
      .addCase(updateKategorie.fulfilled, (state, action) => upsert(state.kategorien, action.payload))
      .addCase(deleteKategorie.fulfilled, (state, action) => {
        const geloescht = state.kategorien.find((k) => k.uuid === action.payload);
        state.kategorien = state.kategorien.filter((k) => k.uuid !== action.payload);
        // Kategorie ist geloescht -> Zuordnung bei betroffenen Aufgaben lokal entfernen.
        if (geloescht) {
          state.todos.forEach((t) => {
            if (t.kategorieId === geloescht.id) t.kategorieId = null;
          });
        }
      })
      .addCase(fetchVorlagen.fulfilled, (state, action) => {
        state.vorlagen = action.payload;
      })
      .addCase(createVorlage.fulfilled, (state, action) => upsert(state.vorlagen, action.payload))
      .addCase(updateVorlage.fulfilled, (state, action) => upsert(state.vorlagen, action.payload))
      .addCase(deleteVorlage.fulfilled, (state, action) => {
        state.vorlagen = state.vorlagen.filter((v) => v.uuid !== action.payload);
      });
  },
});

// ── Selektoren ──────────────────────────────────────────
export const selectTodos = (state) => state.todo.todos;
export const selectKategorien = (state) => state.todo.kategorien;
export const selectVorlagen = (state) => state.todo.vorlagen;
export const selectTodoStatus = (state) => state.todo.status;
export const selectTodoError = (state) => state.todo.error;

export default todoSlice.reducer;
